import { useConversationStore } from "@/store/conversation"
import type {
  ChatItem,
  MessageItem,
  ContentMetadata,
  MessageSegment,
  FileUploadResponse,
  FileDeleteRequest,
  SendMessageRequest
} from "@/types/app"
import {
  deleteConversations,
  getConversationMessages,
  getConversations,
  renameConversation,
  submitFeedback,
  getAgentFaqInputOptions,
  getAgentFaqList,
  getFeedbackTags,
  uploadFile,
  deleteFile
} from "@/api/conversation"
import { ssePost } from "@/assets/js/utils/axios"
import { parseCardMetadata } from "@/components/agent/cards"
import { chatMessagesApi } from "@/api"
import { v4 as uuidv4 } from "uuid"
import { useSobotService } from "./sobotService"

// 转人工标识
const SERVICE_HUMAN_TAG = "<service-human>"

// 使用模块级全局变量实现单例控制，确保跨组件能精准终止同一个请求
// 记录当前正在回答的消息ID
let currentRespondingMessageId = ""
// 用于管理每个请求的 AbortController
const requestMap = new Map<string, AbortController>()
let currentRequestId: string | null = null

export function useConversationService() {
  const store = useConversationStore()

  /**
   * 获取所有历史对话（用于历史记录弹窗，分页）
   * @param offset 偏移量
   * @param limit 每页数量
   */
  async function fetchAllConversations(offset: number, limit: number) {
    // 调用getConversations时不传递busi_agent_id来获取全部历史
    return getConversations(undefined, offset, limit)
  }

  // 加载对话列表
  async function loadConversations() {
    try {
      store.setLoading(true)
      const busi_agent_id = sessionStorage.getItem("busi_agent_id")
      const data = await getConversations(busi_agent_id || "")
      // 只设置会话列表，不自动创建新会话
      store.setConversationList(data.list)
    } catch (error) {
      console.error("获取对话列表失败:", error)
      store.setConversationList([])
    } finally {
      store.setLoading(false)
    }
  }

  // 加载对话消息
  async function loadMessages() {
    if (store.currentConversationId === "-1" || store.isNewConversation) store.setChatList([])
    try {
      store.setLoading(true)
      const res: any = await getConversationMessages(store.currentConversationId)
      const messages = res

      // 按message_id升序排序
      messages.sort((a: MessageItem, b: MessageItem) => {
        // message_id 可能是字符串，转为数字比较
        return Number(a.message_id) - Number(b.message_id)
      })

      if (messages && messages.length > 0) {
        const newChatList: ChatItem[] = []

        messages.forEach((item: MessageItem) => {
          if (item.role === "user") {
            // 用户消息：直接使用 message_files，不进行过滤
            newChatList.push({
              id: item.message_id,
              messageId: item.message_id,
              content: item.content,
              isAnswer: false,
              message_files: item.message_files || []
            })
          } else if (item.role === "assistant") {
            // 助手消息：直接使用 message_files，不进行过滤
            newChatList.push({
              id: item.message_id,
              messageId: item.message_id,
              content: item.content,
              isAnswer: true,
              isLoading: false,
              isCompleted: true,
              feedback: item.feedback,
              message_files: item.message_files || []
            })
          }
        })

        store.setChatList(newChatList)
      } else {
        store.setChatList([])
      }
    } catch (error) {
      console.error("获取消息失败:", error)
    } finally {
      store.setLoading(false)
    }
  }

  // 发送消息并处理流式响应
  async function sendMessage(
    message: string,
    fileIds: string[] = [],
    fileInfos: any[] = [],
    considering: boolean = true,
    refresh: boolean = false
  ) {
    // 如果处于人工客服模式，通过智齿客服发送
    if (store.isHumanService) {
      const sobotService = useSobotService()
      // 添加用户消息到列表
      const userMsgId = `user-human-${Date.now()}`
      const userMsg: ChatItem = {
        id: userMsgId,
        content: message,
        isAnswer: false,
        msgSource: "human",
        message_files: fileInfos
      }
      store.setChatList([...store.chatList, userMsg])
      await sobotService.sendHumanMessage(store.currentConversationId, message, fileIds)
      return
    }

    if (!message.trim() && fileIds.length === 0) return
    if (store.isResponding) {
      await stopResponse()
    }

    const userMessageId = `user-message-${Date.now()}`
    const userMessage: ChatItem = {
      id: userMessageId,
      content: message,
      isAnswer: false,
      message_files: fileInfos
    }

    const placeholderId = `answer-${Date.now()}`
    const placeholderMessage: ChatItem = {
      id: placeholderId,
      content: "思考中...",
      isAnswer: true,
      isLoading: true,
      isCompleted: false
    }

    // 合并并按id升序排序后再setChatList
    let updatedChatList
    if (refresh) updatedChatList = [...store.chatList, placeholderMessage]
    else updatedChatList = [...store.chatList, userMessage, placeholderMessage]
    updatedChatList.sort((a, b) => Number(a.id) - Number(b.id))
    store.setChatList(updatedChatList)
    store.setIsResponding(true)
    currentRespondingMessageId = placeholderId

    let lastResponseContent = ""
    let rawContentBuffer = "" // 累积原始内容，用于检测被流式分割的标识
    let pendingBuffer = "" // 暂存可能是转人工标识前缀的内容
    const messageSegments: MessageSegment[] = [] // 卡片/文案按流式到达顺序分段（卡片+文案组合消息按此顺序混排展示）

    // 文本入段：追加到末尾文案分段（无则新建），与卡片段一起保序
    const appendResponseText = (text: string) => {
      if (!text) return
      lastResponseContent += text
      const last = messageSegments[messageSegments.length - 1]
      if (last?.type === "text") last.text += text
      else messageSegments.push({ type: "text", text })
    }
    let messageId = ""
    let newConversationId = ""
    let isTransferToHuman = false // 是否检测到转人工标识
    const isNewConversation = store.currentConversationId === "-1"

    const requestId = uuidv4()
    currentRequestId = requestId
    const abortController = new AbortController()
    requestMap.set(requestId, abortController)
    const busi_agent_id = sessionStorage.getItem("busi_agent_id")

    // 转人工统一处理: 移除当前 AI 占位/已部分生成的回答 + 清理请求状态 + 启动转人工轮询
    const enterHumanService = () => {
      const aiMsgId = messageId || placeholderId
      store.setChatList(store.chatList.filter((item) => item.id !== aiMsgId))
      store.resetState()
      currentRespondingMessageId = ""
      requestMap.delete(requestId)
      if (requestId === currentRequestId) currentRequestId = null
      const realConversationId = newConversationId || store.currentConversationId
      const sobotService = useSobotService()
      sobotService.startTransferPolling(realConversationId, busi_agent_id || "")
    }

    // 统一失败处理：写入错误气泡并复位请求状态（onData 错误帧 / onError / 请求异常共用，只生效一次）
    let failureHandled = false
    const markFailed = (content?: string) => {
      if (failureHandled) return
      failureHandled = true
      store.updateChatList(messageId || placeholderId, {
        content: content || "抱歉，消息发送失败，请重试",
        isAnswer: true,
        isLoading: false,
        isCompleted: true,
        isError: true,
        segments: undefined // 清除已写入的卡片分段，否则 segments 渲染分支优先于文案，错误文案无法展示
      })
      store.resetState()
      currentRespondingMessageId = ""
      requestMap.delete(requestId)
      if (requestId === currentRequestId) currentRequestId = null
    }

    try {
      const requestBody: SendMessageRequest = {
        conversation_id: store.currentConversationId !== "-1" ? store.currentConversationId : undefined,
        message: message,
        busi_agent_id: busi_agent_id || "",
        file_ids: fileIds, // 添加文件ID列表
        considering: considering // 添加语境联想开关
      }

      await new Promise<void>((resolve, reject) => {
        ssePost(
          chatMessagesApi,
          { body: { ...requestBody }, signal: abortController.signal },
          {
            onData: (content: string, moreInfo: any) => {
              // 优先取 SSE 响应中的 conversation_id（新会话首次返回会带上真实ID）
              if (moreInfo.conversationId && !newConversationId) {
                newConversationId = moreInfo.conversationId
                // 如果当前是新对话，立即更新 store 的 conversation_id
                if (isNewConversation && store.currentConversationId === "-1") {
                  store.setCurrentConversationId(newConversationId)
                }
              }

              // 累积原始内容，用于检测被流式分割的标识
              if (content) rawContentBuffer += content

              // 检测完整转人工标识：仅打标记并中止流，后续清理与转人工启动统一在 onError(AbortError) 处理
              if (rawContentBuffer.includes(SERVICE_HUMAN_TAG)) {
                isTransferToHuman = true
                pendingBuffer = ""
                abortController.abort()
                enterHumanService()
                return
              }

              // 如果累积内容末尾是 <service-human> 的不完整前缀，暂存不展示
              const tagIdx = rawContentBuffer.lastIndexOf("<")
              if (tagIdx !== -1) {
                const suffix = rawContentBuffer.slice(tagIdx)
                if (suffix !== SERVICE_HUMAN_TAG && SERVICE_HUMAN_TAG.startsWith(suffix)) {
                  pendingBuffer += content || ""
                  return
                }
              }

              // 确认不是转人工，补回暂存内容
              if (pendingBuffer) {
                appendResponseText(pendingBuffer)
                pendingBuffer = ""
              }

              if (moreInfo?.errorMessage) {
                markFailed(moreInfo.errorMessage)
                return reject(new Error(moreInfo.errorMessage))
              }

              if (moreInfo.messageId && !messageId) {
                messageId = moreInfo.messageId
                // 当收到后台返回的messageId时，更新消息的ID
                store.updateMessageId(placeholderId, messageId, {
                  messageId: messageId
                })
                // 更新当前响应的消息ID
                currentRespondingMessageId = messageId
              }
              if (moreInfo.taskId) store.setCurrentTaskId(moreInfo.taskId)

              if (content) appendResponseText(content)

              // 解析工具调用元数据中的自定义卡片，按到达顺序入段（route 取值见 cards/index 的 CARD_ROUTE）
              parseCardMetadata(moreInfo.contentMetadata).forEach((card) =>
                messageSegments.push({ type: "card", card })
              )

              // 使用当前的消息ID（可能是placeholderId或真实的messageId）
              const currentMessageId = messageId || placeholderId
              store.updateChatList(currentMessageId, {
                id: currentMessageId,
                messageId,
                content: lastResponseContent,
                isAnswer: true,
                isLoading: false,
                isCompleted: moreInfo.done === true, // 根据后端返回的 done 字段设置完成状态
                // 含卡片段才写入 segments（纯文案走 content 渲染）；浅拷贝分段保证响应式（段内 text 在流式期间持续追加）
                ...(messageSegments.some((seg) => seg.type === "card")
                  ? { segments: messageSegments.map((seg) => ({ ...seg })) }
                  : {})
              })
            },
            onCompleted: async () => {
              // 由转人工触发的中止: 统一进入人工流程, 不再走 AI 完成逻辑
              if (isTransferToHuman) {
                resolve()
                return
              }
              currentRespondingMessageId = ""
              // 设置响应完成状态
              const currentMessageId = messageId || placeholderId
              store.updateChatList(currentMessageId, {
                isLoading: false,
                isCompleted: true
              })
              // 新会话，发送成功后刷新历史会话并切换到新会话
              if (isNewConversation && newConversationId) {
                // 只刷新历史会话列表
                const busi_agent_id = sessionStorage.getItem("busi_agent_id")
                const data = await getConversations(busi_agent_id || "")
                store.setConversationList(data.list)
                // 默认选中第一条
                if (data && data.list.length > 0) {
                  store.setCurrentConversationId(data.list[0].conversation_id)
                }
              }
              // 只有当完成的请求是当前活跃请求时，才重置全局响应状态
              if (store.isResponding && requestId === currentRequestId) {
                store.resetState()
              }
              requestMap.delete(requestId)
              if (requestId === currentRequestId) {
                currentRequestId = null
              }
              resolve()
            },
            onError: (err: any) => {
              if (err && String(err).includes("AbortError")) {
                resolve()
                return
              }
              console.error("流式请求错误:", err)
              markFailed()
              reject(new Error(err))
            }
          }
        )
      })
    } catch (error) {
      console.error("发送消息失败:", error)
      markFailed()
    }
  }

  // 停止响应
  async function stopResponse() {
    try {
      if (currentRequestId && requestMap.has(currentRequestId)) {
        const controller = requestMap.get(currentRequestId)
        controller?.abort()
        requestMap.delete(currentRequestId)
        // 设置当前消息 isStopped
        if (currentRespondingMessageId) {
          store.updateChatList(currentRespondingMessageId, {
            isStopped: true,
            isLoading: false,
            isCompleted: true // 停止时也标记为完成
          })
        }
        store.resetState()
        currentRespondingMessageId = ""
        currentRequestId = null
      }
    } catch (error) {
      console.error("停止响应失败:", error)
      store.resetState()
      currentRespondingMessageId = ""
      currentRequestId = null
    }
  }
  //查询点赞点踩标签列表 - 简单缓存
  async function fetchFeedbackTags(feedback: string) {
    // 如果已经加载过，直接返回缓存
    if (store.feedbackTagsLoaded) {
      return { data: store.feedbackTags }
    }
    try {
      const response = await getFeedbackTags(feedback)
      // 缓存结果
      if (response.data) {
        store.setFeedbackTags(response.data)
      }
      return response
    } catch (error) {
      console.error("获取反馈标签失败:", error)
      throw error
    }
  }
  // 提交消息反馈
  async function submitMessageFeedback(
    messageId: string,
    feedback: "0" | "1" | "2",
    contentMetadata?: ContentMetadata
  ) {
    try {
      await submitFeedback(messageId, feedback, contentMetadata)
      // 通过messageId查找对应的消息来更新反馈状态
      const message = store.chatList.find((item) => item.messageId === messageId)
      if (message) {
        store.updateChatList(message.id, {
          feedback: feedback,
          content_metadata: contentMetadata
        })
      }
    } catch (error) {
      console.error("提交反馈失败:", error)
    }
  }

  // 重命名对话
  async function renameConversationById(conversation_id: string, newTitle: string) {
    try {
      await renameConversation(conversation_id, newTitle)
      // 更新对话列表中的对话名称
      store.updateConversation(conversation_id, newTitle)
    } catch (error) {
      console.error("重命名对话失败:", error)
    }
  }

  // 删除对话
  async function deleteConversationById(conversation_ids: string | string[]) {
    try {
      // 确保conversation_ids是数组
      const ids = Array.isArray(conversation_ids) ? conversation_ids : [conversation_ids]

      await deleteConversations(ids)

      // 如果删除的会话中包含当前会话，则切换到新会话
      if (ids.includes(store.currentConversationId)) {
        store.createNewConversation()
        // 同时清除本地存储的 lastConversationId
        sessionStorage.removeItem("lastConversationId")
      }

      // 从对话列表中移除所有被删除的对话
      store.removeConversation(ids)
    } catch (error) {
      console.error("删除对话失败:", error)
    }
  }

  /**
   * 切换智能体获取新历史会话
   * @param agentId 智能体ID
   */
  async function switchAgent(agentId: string) {
    try {
      store.setLoading(true)
      const data = await getConversations(agentId)
      store.setConversationList(data.list)
      store.createNewConversation()
    } catch (error) {
      console.error("切换智能体失败:", error)
      store.setConversationList([])
    } finally {
      store.setLoading(false)
    }
  }

  // 创建新对话
  function startNewConversation() {
    store.createNewConversation()
  }

  // 切换对话
  async function switchConversation(conversation_id: string) {
    // 切换会话前先清理人工客服残留: 旧 cid 的轮询若不停, 响应会写入新会话的聊天列表
    if (store.isHumanService) {
      useSobotService().stopAllSobotActivity()
    }
    store.setCurrentConversationId(conversation_id)
    await loadMessages()
  }

  // 初始化
  async function initialize() {
    await loadConversations()
    // 获取上次的会话ID
    const lastConversationId = store.getLastConversationId()
    // 如果存在上次的会话ID，则切换到该会话
    if (lastConversationId !== "-1") {
      await switchConversation(lastConversationId)
    } else {
      // 如果没有上次的会话，则创建新会话
      store.createNewConversation()
    }
  }

  /**
   * 获取高频问题输入选项
   */
  async function fetchAgentFaqInputOptions() {
    return getAgentFaqInputOptions()
  }

  /**
   * 获取智能体高频问题列表
   */
  async function fetchAgentFaqList(busi_agent_id: string) {
    return getAgentFaqList(busi_agent_id)
  }

  /**
   * 上传文件
   * @param {File[]} files 要上传的文件数组
   * @returns {Promise<FileUploadResponse[]>} 上传结果数组
   */
  async function uploadFileService(files: File[]): Promise<FileUploadResponse[]> {
    try {
      const allResults: FileUploadResponse[] = []
      // 循环上传每个文件
      for (const file of files) {
        const result = await uploadFile(file)
        // 将单个文件的结果添加到数组中
        allResults.push(result)
      }
      return allResults
    } catch (error) {
      console.error("文件上传失败:", error)
      throw error
    }
  }

  /**
   * 删除文件
   * @param {string} fileId 要删除的文件ID
   * @returns {Promise<void>} 删除结果
   */
  async function deleteFileService(fileId: string): Promise<void> {
    try {
      const fileData: FileDeleteRequest = {
        file_id: fileId
      }
      await deleteFile(fileData)
    } catch (error) {
      console.error("文件删除失败:", error)
      throw error
    }
  }

  return {
    loadConversations,
    loadMessages,
    sendMessage,
    stopResponse,
    fetchFeedbackTags,
    submitMessageFeedback,
    renameConversationById,
    deleteConversationById,
    switchAgent,
    startNewConversation,
    switchConversation,
    initialize,
    fetchAllConversations,
    fetchAgentFaqInputOptions,
    fetchAgentFaqList,
    uploadFileService,
    deleteFileService
  }
}
